// apps/nos/client/src/components/Workbench/IngredientSearch/IngredientSearchRow.tsx
import type React from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import type { IngredientRow } from "@/utils/types/ingredient";

type Props = {
  row: IngredientRow;
  isIn: boolean;
  onAdd: () => void;
  style: React.CSSProperties;
};

export function IngredientSearchRow({ row, isIn, onAdd, style }: Props) {
  const isMixture = row.kind === "food";
  return (
    <button
      type="button"
      onClick={onAdd}
      disabled={isIn}
      title={
        isMixture
          ? "Mixture · multi-compound food"
          : "Compound · single molecule"
      }
      className={cn("is-result-row", isIn && "in-flask")}
      style={style}
    >
      <div
        style={{
          width: isMixture ? 7 : 4,
          height: 24,
          flexShrink: 0,
          display: "flex",
          gap: isMixture ? 1 : 0,
        }}
      >
        <div
          style={{
            flex: 1,
            borderRadius: 2,
            background: row.family?.color ?? "var(--fam-default)",
          }}
        />
        {isMixture && (
          <div
            style={{
              flex: 1,
              borderRadius: 2,
              background: row.family?.color ?? "var(--fam-default)",
              opacity: 0.45,
            }}
          />
        )}
      </div>
      <div className="vstack" style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            fontStyle: isMixture ? "italic" : "normal",
            fontFamily: isMixture
              ? "var(--font-serif, 'Noto Serif', serif)"
              : "inherit",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.name}
        </span>
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: "var(--ink-4)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.identifiers.casPrimary ?? "—"}
          {row.identifiers.casExtraCount > 0
            ? ` +${row.identifiers.casExtraCount}`
            : ""}
          {row.identifiers.catalogNo ? ` · ${row.identifiers.catalogNo}` : ""}
        </span>
      </div>
      {row.deltaLoss != null && (
        <span
          className="mono"
          style={{
            fontSize: 11,
            fontWeight: 600,
            // Sign-aware tint: negative (improving) → green, positive (rare) → red,
            // zero or near-zero → muted ink.
            color:
              row.deltaLoss < -0.001
                ? "var(--accent-green, #16a34a)"
                : row.deltaLoss > 0.001
                  ? "var(--accent-red, #dc2626)"
                  : "var(--ink-4)",
            marginLeft: 6,
            marginRight: 4,
            whiteSpace: "nowrap",
          }}
          title={`Δ-Loss vs current formulation: ${row.deltaLoss.toFixed(4)}`}
        >
          {row.deltaLoss >= 0 ? "+" : ""}
          {row.deltaLoss.toFixed(2)}
        </span>
      )}
      {isIn ? <Icon name="check" size={14} /> : <Icon name="plus" size={14} />}
    </button>
  );
}
